import { ProbedQuestionModel } from "../../../models/ProbedQuestionModel";
import {
    ADD_PROBED_QUESTION,
    UPDATE_PROBED_QUESTION,
    CLEAR_PROBED_QUESTIONS
} from "../../actions";
import { ActionTypes } from "../../actions/types";

export const probedQuestionsSliceReducer = (
    state: { [mainVariableId: string]: ProbedQuestionModel[] } = {},
    action: ActionTypes
): { [mainVariableId: string]: ProbedQuestionModel[] } => {
    switch (action.type) {
        // Handle adding a new probed question
        case ADD_PROBED_QUESTION: {
            const probedQuestion: ProbedQuestionModel = action.payload;
            const existingQuestions = state[probedQuestion.mainVariableId] || [];

            // Never store the same probe twice. Concurrent generation runs can each
            // produce a probe with the same deterministic id (e.g. VAR_AI_PROBING_1),
            // which would render as a duplicate. Dedupe by id. (DEV-98341)
            if (existingQuestions.some(q => q.id === probedQuestion.id)) {
                return state;
            }

            // Add the new question and ensure they're sorted by order
            const updatedQuestions = [...existingQuestions, probedQuestion]
                .sort((a, b) => a.order - b.order);
            
            return {
                ...state,
                [probedQuestion.mainVariableId]: updatedQuestions
            };
        }

        // Handle updating an existing probed question (e.g., when user answers it)
        case UPDATE_PROBED_QUESTION: {
            const { mainVariableId, id, answer } = action.payload;
            const existingQuestions = state[mainVariableId] || [];
            
            // Find the updated question
            const updatedQuestionIndex = existingQuestions.findIndex(q => q.id === id);
            
            if (updatedQuestionIndex === -1) {
                return state; // Question not found
            }
            
            const updatedQuestion = {
                ...existingQuestions[updatedQuestionIndex],
                answer
            };
            
            // Implement cascade delete: remove questions with higher order
            // If the answer is being changed (not just initially set)
            const questionHadPreviousAnswer = existingQuestions[updatedQuestionIndex].answer !== "";
            const isAnswerChanged = answer !== existingQuestions[updatedQuestionIndex].answer;
            
            if (questionHadPreviousAnswer && isAnswerChanged) {
                // Only keep questions with order less than or equal to the updated one
                const questionsToKeep = existingQuestions
                    .filter(q => q.order <= updatedQuestion.order)
                    .map(q => q.id === id ? updatedQuestion : q);
                
                return {
                    ...state,
                    [mainVariableId]: questionsToKeep
                };
            }
            
            // Simply update the question if no cascade delete is needed
            return {
                ...state,
                [mainVariableId]: existingQuestions.map(q => 
                    q.id === id ? updatedQuestion : q
                )
            };
        }

        // Clear all probed questions for a specific variable
        case CLEAR_PROBED_QUESTIONS: {
            const { mainVariableId } = action.payload;
            const { [mainVariableId]: _, ...remainingQuestions } = state;
            
            return remainingQuestions;
        }

        default:
            return state;
    }
}; 